import { ChangeDetectionStrategy, Component, effect } from '@angular/core';
import { BackendService } from '@app/@shared/backend.service';
import { ProjectService } from '@app/shell/header/header.project.service';
import { CAPTURE_NAV_CONFIG } from '@shared/analysis-nav/nav-configs';
import { NavConfig } from '@shared/analysis-nav/analysis-nav.component';
import { TableExportService } from '@shared/table-export.service';
import type { ExportColumn, ExportRow } from '@shared/table-export.service';
import {
  normalizeNumericTableRows,
  isNumericTableColumn,
  SMALL_TABLE_FILTER_BUTTON_PROPS,
} from '@shared/table-filter.utils';
import { FilterMatchMode } from '@openng/optimus-ui/api';
import type { FilterMetadata } from '@openng/optimus-ui/api';
import type { Table, TableFilterEvent } from '@openng/optimus-ui/table';

type TableFilters = Record<string, FilterMetadata | FilterMetadata[]>;

interface QcData {
  desc: string;
  cols: ExportColumn[];
  data: ExportRow[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isQcData(value: unknown): value is QcData {
  if (
    !isRecord(value) ||
    typeof value['desc'] !== 'string' ||
    !Array.isArray(value['cols']) ||
    !Array.isArray(value['data'])
  ) {
    return false;
  }

  return (
    value['cols'].every(
      (column: unknown) =>
        isRecord(column) && typeof column['title'] === 'string' && typeof column['data'] === 'string',
    ) && value['data'].every((row: unknown) => isRecord(row))
  );
}

@Component({
  // This component is activated through routing and is not used as an element selector.
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'gene-stats',
  templateUrl: './gene-stats.component.html',
  styleUrls: ['./gene-stats.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GeneStatsComponent {
  isLoading = false;
  projectViewData: QcData = { desc: '', cols: [], data: [] };
  selectedProjectID = '';
  readonly pageSizes = [10, 20, 50, 200, 500];
  exportMessage = '';
  filters: TableFilters = this.createTableFilters();
  globalFilter = '';
  filteredRows: ExportRow[] = [];
  readonly filterButtonProps = SMALL_TABLE_FILTER_BUTTON_PROPS;

  private projectRequestId = 0;

  get navConfig(): NavConfig {
    return CAPTURE_NAV_CONFIG;
  }

  get analysisType(): 'capture' | 'flairr' {
    return 'capture';
  }

  get globalFilterFields(): string[] {
    return this.projectViewData.cols.map((column) => column.data);
  }

  constructor(
    private backendService: BackendService,
    public projectService: ProjectService,
    private exportService: TableExportService,
  ) {
    effect(() => {
      this.setProject();
    });
  }

  setProject(): void {
    const projectDetails = this.projectService.selectedProjectDetails();
    if (projectDetails === null) {
      this.projectRequestId += 1;
      this.selectedProjectID = '';
      this.isLoading = false;
      this.exportMessage = '';
      this.projectViewData = { desc: '', cols: [], data: [] };
      this.filteredRows = [];
      this.filters = this.createTableFilters();
      this.globalFilter = '';
      return;
    }

    const requestId = ++this.projectRequestId;
    this.selectedProjectID = projectDetails.project_id;
    this.isLoading = true;
    this.exportMessage = '';
    this.projectViewData = { desc: '', cols: [], data: [] };

    this.backendService.getProjectQCData(this.selectedProjectID, 'Capture Probe').subscribe({
      next: (response: unknown) => {
        if (requestId !== this.projectRequestId) {
          return;
        }

        this.isLoading = false;
        if (!isRecord(response) || Number(response['status']) !== 200) {
          return;
        }

        if (!isQcData(response['reply'])) {
          this.exportMessage = 'The QC statistics response was not in the expected format.';
          console.error('The QC statistics endpoint returned an invalid response.', response);
          return;
        }

        const qcData = response['reply'];
        this.projectViewData = {
          ...qcData,
          data: normalizeNumericTableRows(qcData.cols, qcData.data),
        };
        this.filteredRows = this.projectViewData.data;
        this.filters = this.createTableFilters();
        this.globalFilter = '';
      },
      error: (error: unknown) => {
        if (requestId !== this.projectRequestId) {
          return;
        }

        this.isLoading = false;
        console.error('Failed to load project QC statistics.', error);
      },
    });
  }

  isNumericColumn(column: ExportColumn): boolean {
    return isNumericTableColumn(column, this.projectViewData.data);
  }

  onGlobalFilterChange(event: Event, table: Table): void {
    this.globalFilter = (event.target as HTMLInputElement).value;
    table.filterGlobal(this.globalFilter, FilterMatchMode.CONTAINS);
  }

  onTableFilter(event: TableFilterEvent): void {
    this.filteredRows = (event.filteredValue as ExportRow[] | undefined) ?? this.projectViewData.data;
  }

  async copyTable(): Promise<void> {
    await this.runExport('copy', () => this.exportService.copy(this.projectViewData.cols, this.filteredRows));
  }

  async exportExcel(): Promise<void> {
    await this.runExport('Excel', () =>
      this.exportService.exportExcel(this.projectViewData.cols, this.filteredRows, 'qc-stats'),
    );
  }

  async exportPdf(): Promise<void> {
    await this.runExport('PDF', () =>
      this.exportService.exportPdf(this.projectViewData.cols, this.filteredRows, 'qc-stats'),
    );
  }

  private async runExport(label: string, action: () => Promise<void>): Promise<void> {
    this.exportMessage = '';
    try {
      await action();
      this.exportMessage = label === 'copy' ? 'Table copied to clipboard.' : `${label} export downloaded.`;
    } catch (error) {
      this.exportMessage = `Could not complete ${label === 'copy' ? 'copy' : `${label} export`}.`;
      console.error(`Failed to complete ${label} for project QC statistics.`, error);
    }
  }

  private createTableFilters(): TableFilters {
    return { global: { value: null, matchMode: FilterMatchMode.CONTAINS } };
  }
}
